// Legacy client-side gallery storage.
//
// The Album feature originally stored everything in the browser's
// localStorage since there was no backend route for it. Now that
// /gallery exists on the server (see utils/api.jsx), this module is only
// used to read whatever old data is still sitting in localStorage from
// before, so it can be offered as a one-time migration to the real
// account-based storage. Nothing writes to localStorage anymore.

const STORAGE_PREFIX = "wtwr_gallery_";

const keyFor = (userId) => `${STORAGE_PREFIX}${userId || "guest"}`;

export const loadGalleryItems = (userId) => {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    return raw ? JSON.parse(raw) : [];
  } catch (err) {
    console.error("Failed to load legacy gallery items:", err);
    return [];
  }
};

export const clearGalleryItems = (userId) => {
  try {
    localStorage.removeItem(keyFor(userId));
  } catch (err) {
    console.error("Failed to clear legacy gallery items:", err);
  }
};

// Debug helper: lists every gallery-related key currently in localStorage,
// with item counts. Useful for spotting leftover data under an unexpected
// key (e.g. a "guest" bucket from before an ID-related bug was fixed).
export const listAllGalleryKeys = () => {
  const results = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_PREFIX)) {
        let count = 0;
        try {
          const parsed = JSON.parse(localStorage.getItem(key));
          count = Array.isArray(parsed) ? parsed.length : 0;
        } catch {
          count = -1;
        }
        results.push({ key, count });
      }
    }
  } catch (err) {
    console.error("Failed to list gallery keys:", err);
  }
  return results;
};

// Converts an old stored data URL (from a legacy upload) back into a real
// File object, so it can be re-uploaded through the normal /gallery/upload
// endpoint during migration.
export const dataUrlToFile = async (dataUrl, filename, mimeType) => {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  return new File([blob], filename, { type: mimeType || blob.type });
};
